import * as THREE from 'three';
import { PlayerPhysicsState, PhysicsEngine } from '../engine/Physics';
import {
  PLAYER_HEIGHT, CROUCH_HEIGHT, PLAYER_RADIUS, EYE_HEIGHT, CROUCH_EYE_HEIGHT,
} from '../shared/constants';

export class PlayerRenderer {
  private playerMeshes = new Map<string, THREE.Group>();
  private scene: THREE.Scene;
  private nameTagCanvas: HTMLCanvasElement;
  private nameTagCtx: CanvasRenderingContext2D;

  constructor(scene: THREE.Scene) {
    this.scene = scene;
    this.nameTagCanvas = document.createElement('canvas');
    this.nameTagCanvas.width = 512;
    this.nameTagCanvas.height = 128;
    this.nameTagCtx = this.nameTagCanvas.getContext('2d')!;
  }

  createPlayer(id: string, name: string, isLocal: boolean): THREE.Group {
    const group = new THREE.Group();
    group.name = `player_${id}`;

    const bodyMat = new THREE.MeshStandardMaterial({
      color: isLocal ? 0x00e5ff : 0x536dfe,
      roughness: 0.55,
      metalness: 0.1,
    });

    const torso = new THREE.Mesh(
      new THREE.BoxGeometry(0.88, 1.08, 0.54),
      bodyMat
    );
    torso.name = 'torso';
    torso.castShadow = true;
    torso.receiveShadow = true;
    group.add(torso);

    const headMat = new THREE.MeshStandardMaterial({
      color: isLocal ? 0x00e676 : 0xffc400,
      roughness: 0.6,
      metalness: 0.05,
    });

    const head = new THREE.Mesh(
      new THREE.BoxGeometry(0.56, 0.52, 0.56),
      headMat
    );
    head.name = 'head';
    head.castShadow = true;
    group.add(head);

    const eyeMat = new THREE.MeshStandardMaterial({
      color: isLocal ? 0xffffff : 0xffffff,
      emissive: isLocal ? 0x00e5ff : 0xff1744,
      emissiveIntensity: 0.8,
    });

    const leftEye = new THREE.Mesh(
      new THREE.BoxGeometry(0.08, 0.06, 0.04),
      eyeMat
    );
    leftEye.position.set(-0.12, 0.04, 0.28);
    head.add(leftEye);

    const rightEye = new THREE.Mesh(
      new THREE.BoxGeometry(0.08, 0.06, 0.04),
      eyeMat
    );
    rightEye.position.set(0.12, 0.04, 0.28);
    head.add(rightEye);

    const tag = this.createNameTag(name);
    tag.name = 'nameTag';
    group.add(tag);

    group.visible = !isLocal;
    this.scene.add(group);
    this.playerMeshes.set(id, group);
    return group;
  }

  private createNameTag(name: string): THREE.Sprite {
    const ctx = this.nameTagCtx;
    ctx.clearRect(0, 0, 512, 128);
    ctx.fillStyle = 'rgba(10,11,14,0.8)';
    this.roundRect(ctx, 16, 16, 480, 96, 14);
    ctx.fill();
    ctx.strokeStyle = 'rgba(0,229,255,0.3)';
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.fillStyle = '#eef0f6';
    ctx.font = '800 40px Inter, Arial, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(name, 256, 64, 420);

    const texture = new THREE.CanvasTexture(this.nameTagCanvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    const material = new THREE.SpriteMaterial({
      map: texture,
      transparent: true,
      depthTest: false,
    });
    const sprite = new THREE.Sprite(material);
    sprite.scale.set(2.4, 0.6, 1);
    return sprite;
  }

  private roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
  }

  updatePlayer(
    id: string,
    state: PlayerPhysicsState,
    isLocal: boolean,
    camera: THREE.Camera
  ): void {
    const group = this.playerMeshes.get(id);
    if (!group) return;

    const height = state.crouching ? CROUCH_HEIGHT : PLAYER_HEIGHT;
    group.position.set(state.position.x, state.position.y, state.position.z);
    group.rotation.y = state.yaw;

    const torso = group.getObjectByName('torso') as THREE.Mesh;
    const head = group.getObjectByName('head') as THREE.Mesh;
    const tag = group.getObjectByName('nameTag') as THREE.Sprite;

    if (torso) {
      torso.scale.y = state.crouching ? 0.72 : 1;
      torso.position.y = height * 0.43;
    }
    if (head) {
      head.position.y = height - 0.26;
    }
    if (tag) {
      tag.position.set(0, height + 0.65, 0);
      tag.quaternion.copy(camera.quaternion);
    }

    group.visible = !isLocal;
  }

  removePlayer(id: string): void {
    const group = this.playerMeshes.get(id);
    if (!group) return;
    this.scene.remove(group);
    group.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.geometry.dispose();
        if (Array.isArray(child.material)) {
          child.material.forEach(m => m.dispose());
        } else {
          child.material.dispose();
        }
      }
    });
    this.playerMeshes.delete(id);
  }

  getLocalEyePosition(state: PlayerPhysicsState): THREE.Vector3 {
    const eye = state.crouching ? CROUCH_EYE_HEIGHT : EYE_HEIGHT;
    return new THREE.Vector3(state.position.x, state.position.y + eye, state.position.z);
  }

  dispose(): void {
    for (const [id] of this.playerMeshes) {
      this.removePlayer(id);
    }
  }
}

export class ViewmodelRenderer {
  public group: THREE.Group;
  private weaponMeshes = new Map<string, THREE.Mesh>();
  private punchAnimation = 0;
  private bobAnimation = 0;

  constructor(parent: THREE.Object3D) {
    this.group = new THREE.Group();
    this.group.position.set(0.38, -0.36, -0.65);
    parent.add(this.group);

    const defaultWeapon = new THREE.Mesh(
      new THREE.BoxGeometry(0.12, 0.12, 0.5),
      new THREE.MeshStandardMaterial({ color: 0x2a2e3d, roughness: 0.4, metalness: 0.6 })
    );
    defaultWeapon.name = 'weapon_default';
    this.group.add(defaultWeapon);
  }

  animatePunch(): void {
    this.punchAnimation = 0.2;
  }

  update(dt: number, isMoving: boolean, speed: number): void {
    this.punchAnimation = Math.max(0, this.punchAnimation - dt);
    const t = this.punchAnimation / 0.2;
    this.group.position.z = -0.65 - Math.sin(t * Math.PI) * 0.35;
    this.group.rotation.x = -Math.sin(t * Math.PI) * 0.4;

    if (isMoving) {
      this.bobAnimation += dt * speed * 0.8;
      const bobIntensity = Math.min(speed / 8, 1) * 0.008;
      this.group.position.y = -0.36 + Math.sin(this.bobAnimation * 2) * bobIntensity;
      this.group.position.x = 0.38 + Math.cos(this.bobAnimation) * bobIntensity * 0.5;
    } else {
      this.bobAnimation = 0;
      this.group.position.y += (-0.36 - this.group.position.y) * dt * 8;
      this.group.position.x += (0.38 - this.group.position.x) * dt * 8;
    }
  }

  dispose(): void {
    this.group.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.geometry.dispose();
        if (Array.isArray(child.material)) {
          child.material.forEach(m => m.dispose());
        } else {
          child.material.dispose();
        }
      }
    });
  }
}
